import { memo, useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useAtom } from "jotai";
import { useNavigate } from "react-router-dom";
import { Button, Form, Input, DatePicker, message, Upload } from "antd";
import {
  CameraOutlined,
  CheckCircleFilled,
  LockOutlined,
  ReloadOutlined,
  DeleteOutlined,
  SafetyCertificateOutlined,
  MailOutlined,
  PhoneOutlined,
  SaveOutlined,
  UserOutlined,
  AppstoreOutlined,
  CloudServerOutlined,
  DesktopOutlined,
  CompassOutlined,
  BlockOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { UI_ScreenStyles } from "../UI_ScreenStyles";
import { createBreadcrumbItems, commonBreadcrumbs } from "~/utils/breadcrumbUtils";
import { userInformationAtom, userACRoles } from "~/stores/UserStore";
import { ProfileAPIs } from "~/common/typescript/apis/ProfileAPIs";
import { User_Base } from "~/common/typescript/models/User";
import { getHashColor } from "~/utils/hashStringUtil";
import { API_URL_ADMIN } from "~/config/config";
import Routes from "~/routes/routes";
import {
  buildProfileFormData,
  getAvatarInitials,
  getUserProductSubscriptions,
  resolveAvatarUrl,
  validateAvatarFile,
  validateVietnamesePhone,
} from "./profileUtils";

const PRODUCT_ICONS: Record<string, JSX.Element> = {
  geocollect: <CloudServerOutlined />,
  geodesktop: <DesktopOutlined />,
  flightPlanner: <CompassOutlined />,
  geocad: <BlockOutlined />,
};

const ProfileScreen = () => {
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useAtom(userInformationAtom);
  const [acRoles] = useAtom(userACRoles);

  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [stagedAvatarFile, setStagedAvatarFile] = useState<File | null>(null);
  const [stagedAvatarPreview, setStagedAvatarPreview] = useState<string | null>(null);

  const applyProfileToForm = useCallback(
    (profile: User_Base) => {
      form.setFieldsValue({
        fullname: profile.fullname || "",
        email: profile.email || "",
        pronouns: profile.pronouns || "",
        job_title: profile.job_title || "",
        department: profile.department || "",
        aboutMe: profile.aboutMe || "",
        phoneNumber: profile.phoneNumber || "",
        birthday: profile.birthday_year
          ? dayjs(`${profile.birthday_year}-${profile.birthday_month || 1}-${profile.birthday_day || 1}`)
          : null,
      });

      const resolved = resolveAvatarUrl(profile.avatar, API_URL_ADMIN);
      setAvatarUrl(resolved);
    },
    [form],
  );

  useEffect(() => {
    ProfileAPIs.remote_getMyProfile()
      .then((profile) => {
        setUserInfo(profile);
        applyProfileToForm(profile);
      })
      .catch((err) => {
        console.error("Failed to load user profile:", err);
      });
  }, [applyProfileToForm, setUserInfo]);

  const handleBeforeUploadAvatar = (file: File) => {
    const check = validateAvatarFile(file);
    if (!check.valid) {
      message.error(check.error || "Tệp ảnh không hợp lệ");
      return Upload.LIST_IGNORE;
    }

    setStagedAvatarFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        setStagedAvatarPreview(reader.result);
      }
    };
    reader.readAsDataURL(file);
    return false;
  };

  const handleClearStagedAvatar = () => {
    setStagedAvatarFile(null);
    setStagedAvatarPreview(null);
  };

  const handleResetProfileForm = () => {
    if (userInfo) {
      applyProfileToForm(userInfo);
    } else {
      form.resetFields();
    }
    handleClearStagedAvatar();
    message.info("Đã khôi phục thông tin ban đầu");
  };

  const handleUpdateProfile = async (values: any) => {
    setLoading(true);
    try {
      const formData = buildProfileFormData(values, stagedAvatarFile);
      await ProfileAPIs.remote_updateProfile(formData);
      message.success("Cập nhật thông tin thành công");

      const updatedProfile = await ProfileAPIs.remote_getMyProfile();
      setUserInfo(updatedProfile);
      applyProfileToForm(updatedProfile);
      if (stagedAvatarPreview && !resolveAvatarUrl(updatedProfile.avatar, API_URL_ADMIN)) {
        setAvatarUrl(stagedAvatarPreview);
      }
      setStagedAvatarFile(null);
      setStagedAvatarPreview(null);
    } catch (error) {
      message.error("Cập nhật thông tin thất bại");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const avatarColors = useMemo(() => {
    const username = userInfo?.username || "user";
    return getHashColor(username);
  }, [userInfo?.username]);

  const displayedAvatarUrl = stagedAvatarPreview || avatarUrl;
  const subscriptions = useMemo(
    () => getUserProductSubscriptions(userInfo),
    [userInfo],
  );

  const isAdmin = useMemo(
    () => Array.isArray(acRoles) && acRoles.includes("TLGEO_ADMIN" as any),
    [acRoles],
  );

  return (
    <UI_ScreenStyles.UI_Wrapper>
      <UI_ScreenStyles.UI_Breadcrumb
        items={createBreadcrumbItems([
          commonBreadcrumbs.home,
          { title: "Thông tin cá nhân", icon: commonBreadcrumbs.settings.icon },
        ])}
      />

      <UI_ScreenStyles.UI_Content_Wrapper>
        <UI_Container>
          {/* 1. Profile Identity Top Panel (design.md Slate/Blue GIS Surface) */}
          <UI_HeaderCard data-testid="profile-header-card">
            <UI_AvatarColumn>
              <Upload
                accept="image/png,image/jpeg,image/webp"
                showUploadList={false}
                beforeUpload={handleBeforeUploadAvatar}
                maxCount={1}
              >
                <UI_AvatarClickable
                  role="button"
                  tabIndex={0}
                  aria-label="Đổi ảnh đại diện"
                  title="Nhấn để đổi ảnh đại diện (JPG, PNG, WebP tối đa 2MB)"
                  data-testid="profile-upload-avatar-btn"
                >
                  {displayedAvatarUrl ? (
                    <UI_AvatarImage
                      src={displayedAvatarUrl}
                      alt="Avatar"
                      data-testid="profile-avatar-image"
                    />
                  ) : (
                    <UI_AvatarFallback
                      data-testid="profile-avatar-fallback"
                      style={{
                        backgroundColor: avatarColors.backgroundColor,
                        color: avatarColors.foregroundColor,
                      }}
                    >
                      {getAvatarInitials(userInfo?.fullname, userInfo?.username)}
                    </UI_AvatarFallback>
                  )}

                  <UI_AvatarHoverMask>
                    <CameraOutlined style={{ fontSize: 18 }} />
                    <span>Đổi ảnh</span>
                  </UI_AvatarHoverMask>

                  <UI_AvatarCameraBadge aria-hidden="true">
                    <CameraOutlined />
                  </UI_AvatarCameraBadge>
                </UI_AvatarClickable>
              </Upload>

              {stagedAvatarFile && (
                <UI_StagedAvatarBar data-testid="profile-staged-avatar-info">
                  <UI_StagedFilePill title={stagedAvatarFile.name}>
                    {stagedAvatarFile.name}
                  </UI_StagedFilePill>
                  <Button
                    size="small"
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={handleClearStagedAvatar}
                    data-testid="profile-clear-avatar-btn"
                  >
                    Hủy chọn ảnh
                  </Button>
                </UI_StagedAvatarBar>
              )}
            </UI_AvatarColumn>

            <UI_UserInfo>
              <UI_NameRow>
                <UI_UserName data-testid="profile-display-name">
                  {userInfo?.fullname || userInfo?.username}
                </UI_UserName>
                <UI_VerifiedPill>
                  <CheckCircleFilled style={{ fontSize: 12 }} />
                  <span>Tài khoản đã xác thực</span>
                </UI_VerifiedPill>
                {isAdmin && (
                  <UI_AdminPill>
                    <SafetyCertificateOutlined style={{ fontSize: 12 }} />
                    <span>Quản trị viên hệ thống</span>
                  </UI_AdminPill>
                )}
              </UI_NameRow>

              {(userInfo?.job_title || userInfo?.department) && (
                <UI_UserMeta data-testid="profile-display-role-dept">
                  {[userInfo?.job_title, userInfo?.department]
                    .filter(Boolean)
                    .join(" • ")}
                </UI_UserMeta>
              )}

              <UI_ContactRow>
                {userInfo?.email && (
                  <UI_ContactChip data-testid="profile-display-email">
                    <MailOutlined style={{ color: "#2563eb" }} />
                    <span>{userInfo.email}</span>
                  </UI_ContactChip>
                )}
                {userInfo?.phoneNumber && (
                  <UI_ContactChip data-testid="profile-display-phone">
                    <PhoneOutlined style={{ color: "#2563eb" }} />
                    <span>{userInfo.phoneNumber}</span>
                  </UI_ContactChip>
                )}
              </UI_ContactRow>
            </UI_UserInfo>

            <UI_HeaderActions>
              <UI_ChangePasswordBtn
                icon={<LockOutlined />}
                onClick={() => navigate(Routes.CHANGE_PASSWORD)}
                data-testid="profile-goto-change-password-btn"
              >
                Đổi mật khẩu
              </UI_ChangePasswordBtn>
            </UI_HeaderActions>
          </UI_HeaderCard>

          {/* 2. Personal Information Card (3-part design.md structure: Header -> Body -> FooterActions) */}
          <UI_PanelCard data-testid="profile-personal-info-section">
            <UI_PanelHeader>
              <UI_PanelHeaderLeft>
                <UI_GradientIconBadge>
                  <UserOutlined />
                </UI_GradientIconBadge>
                <UI_PanelTitleGroup>
                  <UI_PanelTitle>Thông tin cá nhân</UI_PanelTitle>
                  <UI_PanelSubtitle>
                    Cập nhật thông tin hồ sơ của bạn trên hệ thống GeoCloud
                  </UI_PanelSubtitle>
                </UI_PanelTitleGroup>
              </UI_PanelHeaderLeft>
            </UI_PanelHeader>

            <UI_StyledForm
              form={form}
              layout="vertical"
              onFinish={handleUpdateProfile}
              validateTrigger={["onBlur", "onSubmit"]}
            >
              <UI_PanelBody>
                <UI_FormGrid>
                  <Form.Item
                    name="fullname"
                    label="Họ và tên"
                    rules={[
                      { required: true, whitespace: true, message: "Vui lòng nhập họ và tên" },
                    ]}
                  >
                    <Input
                      placeholder="Nhập họ và tên"
                      autoComplete="name"
                      data-testid="profile-input-fullname"
                    />
                  </Form.Item>

                  <Form.Item name="pronouns" label="Danh xưng">
                    <Input
                      placeholder="Ví dụ: Anh / Chị / Ông / Bà"
                      data-testid="profile-input-pronouns"
                    />
                  </Form.Item>

                  <Form.Item name="email" label="Địa chỉ Email">
                    <Input
                      disabled
                      autoComplete="email"
                      spellCheck={false}
                      autoCorrect="off"
                      autoCapitalize="off"
                      data-testid="profile-input-email"
                    />
                  </Form.Item>

                  <Form.Item
                    name="phoneNumber"
                    label="Số điện thoại liên hệ"
                    rules={[
                      {
                        validator: async (_, value) => {
                          if (!validateVietnamesePhone(value)) {
                            throw new Error("Số điện thoại không hợp lệ (ví dụ: 0989340928)");
                          }
                        },
                      },
                    ]}
                  >
                    <Input
                      placeholder="Nhập số điện thoại"
                      autoComplete="tel"
                      inputMode="tel"
                      spellCheck={false}
                      autoCorrect="off"
                      autoCapitalize="off"
                      data-testid="profile-input-phone"
                    />
                  </Form.Item>

                  <Form.Item name="job_title" label="Chức vụ">
                    <Input
                      placeholder="Nhập chức vụ công tác"
                      autoComplete="organization-title"
                      data-testid="profile-input-job-title"
                    />
                  </Form.Item>

                  <Form.Item name="department" label="Phòng ban / Đơn vị">
                    <Input
                      placeholder="Nhập phòng ban hoặc đơn vị"
                      data-testid="profile-input-department"
                    />
                  </Form.Item>
                </UI_FormGrid>

                <Form.Item name="birthday" label="Ngày sinh">
                  <DatePicker
                    style={{ width: "100%" }}
                    format="DD/MM/YYYY"
                    placeholder="Chọn ngày tháng năm sinh"
                    data-testid="profile-input-birthday"
                  />
                </Form.Item>

                <Form.Item name="aboutMe" label="Giới thiệu bản thân" style={{ marginBottom: 4 }}>
                  <Input.TextArea
                    rows={3}
                    maxLength={500}
                    showCount
                    placeholder="Mô tả ngắn gọn về chuyên môn hoặc công việc phụ trách"
                    data-testid="profile-input-about-me"
                  />
                </Form.Item>
              </UI_PanelBody>

              <UI_PanelFooter>
                <Button
                  htmlType="button"
                  icon={<ReloadOutlined />}
                  onClick={handleResetProfileForm}
                  data-testid="profile-reset-btn"
                  style={{ borderRadius: 6, fontSize: 13 }}
                >
                  Khôi phục
                </Button>
                <UI_PrimarySubmitBtn
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={loading}
                  data-testid="profile-save-btn"
                >
                  Lưu thay đổi
                </UI_PrimarySubmitBtn>
              </UI_PanelFooter>
            </UI_StyledForm>
          </UI_PanelCard>

          {/* 3. TLGeo Software Licenses Section (design.md Section 3.4 Card Grid style) */}
          <UI_PanelCard data-testid="profile-subscriptions-section">
            <UI_PanelHeader>
              <UI_PanelHeaderLeft>
                <UI_GradientIconBadge>
                  <AppstoreOutlined />
                </UI_GradientIconBadge>
                <UI_PanelTitleGroup>
                  <UI_PanelTitle>Bản quyền phần mềm TLGeo</UI_PanelTitle>
                  <UI_PanelSubtitle>
                    Trạng thái kích hoạt và thời hạn sử dụng các sản phẩm trong hệ sinh thái TLGeo
                  </UI_PanelSubtitle>
                </UI_PanelTitleGroup>
              </UI_PanelHeaderLeft>
            </UI_PanelHeader>

            <UI_PanelBody>
              <UI_SubscriptionGrid>
                {subscriptions.map((sub) => {
                  const isPaid = sub.tier !== "FREE" && !sub.isBlocked;
                  return (
                    <UI_SubscriptionCard
                      key={sub.key}
                      $active={isPaid}
                      $blocked={sub.isBlocked}
                      data-testid={`profile-sub-${sub.key}`}
                    >
                      <UI_SubHeader>
                        <UI_SubTitleGroup>
                          <UI_SubIconBox $active={isPaid}>
                            {PRODUCT_ICONS[sub.key] || <AppstoreOutlined />}
                          </UI_SubIconBox>
                          <UI_SubName>{sub.name}</UI_SubName>
                        </UI_SubTitleGroup>

                        <UI_TierPill $active={isPaid} $blocked={sub.isBlocked}>
                          {sub.tierLabel}
                        </UI_TierPill>
                      </UI_SubHeader>

                      <UI_SubMetaBox>
                        <CalendarOutlined style={{ color: isPaid ? "#2563eb" : "#94a3b8" }} />
                        <span>
                          {sub.isBlocked
                            ? "Trạng thái: Đang tạm khóa"
                            : sub.expireAt
                            ? `Thời hạn đến: ${dayjs(sub.expireAt).format("DD/MM/YYYY")}`
                            : "Đang sử dụng gói miễn phí"}
                        </span>
                      </UI_SubMetaBox>
                    </UI_SubscriptionCard>
                  );
                })}
              </UI_SubscriptionGrid>
            </UI_PanelBody>
          </UI_PanelCard>
        </UI_Container>
      </UI_ScreenStyles.UI_Content_Wrapper>
    </UI_ScreenStyles.UI_Wrapper>
  );
};

export default memo(ProfileScreen);

const UI_Container = styled.div`
  max-width: 820px;
  margin: 0 auto;
  padding-bottom: 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const UI_HeaderCard = styled.div`
  display: flex;
  align-items: center;
  gap: 22px;
  padding: 20px 24px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 60%, #eff6ff 100%);
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.05);

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const UI_AvatarColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const UI_AvatarHoverMask = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: rgba(15, 23, 42, 0.55);
  color: #ffffff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  font-size: 11px;
  font-weight: 600;
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
`;

const UI_AvatarClickable = styled.div`
  position: relative;
  width: 88px;
  height: 88px;
  border-radius: 50%;
  cursor: pointer;
  user-select: none;

  &:hover ${UI_AvatarHoverMask},
  &:focus-visible ${UI_AvatarHoverMask} {
    opacity: 1;
  }
`;

const UI_AvatarImage = styled.img`
  width: 88px;
  height: 88px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #ffffff;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.12);
  display: block;
`;

const UI_AvatarFallback = styled.div`
  width: 88px;
  height: 88px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: 600;
  border: 3px solid #ffffff;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.12);
`;

const UI_AvatarCameraBadge = styled.span`
  position: absolute;
  right: 0;
  bottom: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: #ffffff;
  border: 2px solid #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  box-shadow: 0 2px 6px rgba(37, 99, 235, 0.35);
  transition: transform 0.15s ease;

  ${UI_AvatarClickable}:hover & {
    transform: scale(1.08);
  }
`;

const UI_StagedAvatarBar = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`;

const UI_StagedFilePill = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #2563eb;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 999px;
  padding: 1px 8px;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UI_UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 0;
`;

const UI_NameRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const UI_UserName = styled.h2`
  margin: 0;
  font-size: 19px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.3;
`;

const UI_VerifiedPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  color: #15803d;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 999px;
  padding: 2px 9px;
  line-height: 1.4;
`;

const UI_AdminPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  color: #2563eb;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 999px;
  padding: 2px 9px;
  line-height: 1.4;
`;

const UI_UserMeta = styled.p`
  margin: 0;
  font-size: 13px;
  font-weight: 500;
  color: #475569;
`;

const UI_ContactRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 2px;
`;

const UI_ContactChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  color: #334155;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 3px 10px;
`;

const UI_HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  margin-left: auto;

  @media (max-width: 640px) {
    margin-left: 0;
    width: 100%;
  }
`;

const UI_ChangePasswordBtn = styled(Button)`
  border-radius: 6px !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  height: 36px !important;
  padding: 0 14px !important;
  color: #1e293b !important;
  border-color: #cbd5e1 !important;
  background: #ffffff !important;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);

  .anticon {
    color: #2563eb;
  }

  &:hover {
    color: #2563eb !important;
    border-color: #3b82f6 !important;
    background: #eff6ff !important;
  }
`;

/* ============================================================================
 * 3-Part Card System per src/components/Map/panels/design.md
 * (1. Slate Gradient Header + 34x34 Icon Badge -> 2. Body -> 3. FooterActions)
 * ============================================================================ */
const UI_PanelCard = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  overflow: hidden;
`;

const UI_PanelHeader = styled.div`
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 1px solid #e2e8f0;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const UI_PanelHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const UI_GradientIconBadge = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
  flex-shrink: 0;
`;

const UI_PanelTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const UI_PanelTitle = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.3;
`;

const UI_PanelSubtitle = styled.p`
  margin: 0;
  font-size: 12px;
  font-weight: 400;
  color: #64748b;
  line-height: 1.35;
`;

const UI_StyledForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 16px;
  }

  .ant-form-item-label > label {
    font-size: 13px;
    font-weight: 600;
    color: #1e293b;
    height: auto;
  }

  .ant-input,
  .ant-picker,
  .ant-input-affix-wrapper {
    border-radius: 6px;
  }
`;

const UI_PanelBody = styled.div`
  padding: 20px;
  background: #ffffff;
`;

const UI_FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 16px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const UI_PanelFooter = styled.div`
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
`;

const UI_PrimarySubmitBtn = styled(Button)`
  border-radius: 6px !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  background: #3b82f6 !important;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.25);

  &:hover {
    background: #2563eb !important;
  }
`;

/* ============================================================================
 * TLGeo Software Licenses Grid per design.md Section 3.4 (Card Grid)
 * ============================================================================ */
const UI_SubscriptionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const UI_SubscriptionCard = styled.div<{ $active?: boolean; $blocked?: boolean }>`
  border: 1px solid #e2e8f0;
  border-top: 3px solid
    ${(p) => (p.$blocked ? "#ef4444" : p.$active ? "#3b82f6" : "#cbd5e1")};
  border-radius: 10px;
  padding: 14px 16px;
  background: #ffffff;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const UI_SubHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const UI_SubTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const UI_SubIconBox = styled.div<{ $active?: boolean }>`
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: ${(p) => (p.$active ? "#eff6ff" : "#f8fafc")};
  border: 1px solid ${(p) => (p.$active ? "#bfdbfe" : "#e2e8f0")};
  color: ${(p) => (p.$active ? "#2563eb" : "#64748b")};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
`;

const UI_SubName = styled.span`
  font-weight: 600;
  font-size: 14px;
  color: #1e293b;
`;

const UI_TierPill = styled.span<{ $active?: boolean; $blocked?: boolean }>`
  font-size: 11px;
  font-weight: 600;
  color: ${(p) => (p.$blocked ? "#dc2626" : p.$active ? "#2563eb" : "#64748b")};
  background: ${(p) => (p.$blocked ? "#fef2f2" : p.$active ? "#eff6ff" : "#f1f5f9")};
  border: 1px solid
    ${(p) => (p.$blocked ? "#fecaca" : p.$active ? "#bfdbfe" : "#e2e8f0")};
  border-radius: 999px;
  padding: 2px 10px;
  white-space: nowrap;
`;

const UI_SubMetaBox = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 12px;
  color: #475569;
`;
